import { Button, CheckboxToggle } from "@bandwith/ui";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import type { KeyboardEvent } from "react";
import { useMemo, useState } from "react";
import { api } from "../lib/api";
import { BackLink } from "./BackLink";
import { DeleteConfirmation } from "./DeleteConfirmation";
import { FormActionButtons } from "./FormActionButtons";
import { SetlistSongsList } from "./SetlistSongsList";
import { SongPicker } from "./SongPicker";

type SongListItem = {
  id: string;
  title: string;
  author?: { name?: string | null } | null;
};

type SetlistSong = {
  id: string;
  songId: string | null;
  title: string | null;
  /** Chosen take for this setlist; null follows the song's primary recording */
  recordingId?: string | null;
  song: {
    recordings?: Array<{ id: string; filename: string; position: number }>;
  } | null;
};

/**
 * A row being edited: a Bandwith song, or just the title of one the band
 * doesn't keep here. `key` identifies the row through reorders; for a song
 * it is the song's id, so per-song recording choices can key on it too.
 */
type EditorEntry = { key: string } & ({ songId: string } | { title: string });

function toEditorEntry(s: SetlistSong): EditorEntry {
  return s.songId
    ? { key: s.songId, songId: s.songId }
    : { key: s.id, title: s.title ?? "" };
}

type SetlistData = {
  id: string;
  name: string;
  notes?: string | null;
  unlisted?: boolean;
  songs: SetlistSong[];
};

type CreateModeProps = {
  mode: "create";
  allSongs: SongListItem[];
  setlist?: never;
};

type EditModeProps = {
  mode: "edit";
  setlist: SetlistData;
  allSongs: SongListItem[];
  onSaved: () => void;
  onCancelled: () => void;
};

type Props = CreateModeProps | EditModeProps;

function handleCmdEnterSubmit(e: KeyboardEvent<HTMLElement>) {
  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
    e.preventDefault();
    e.currentTarget.closest("form")?.requestSubmit();
  }
}

export function SetlistEditor(props: Props) {
  const { allSongs } = props;
  const navigate = useNavigate();

  const isCreateMode = props.mode === "create";
  const setlist = isCreateMode ? null : props.setlist;

  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(setlist?.name ?? "");
  const [notes, setNotes] = useState(setlist?.notes ?? "");
  const [unlisted, setUnlisted] = useState(setlist?.unlisted ?? false);
  const [entries, setEntries] = useState<EditorEntry[]>(
    setlist?.songs.map(toEditorEntry) ?? [],
  );
  const [newTitle, setNewTitle] = useState("");
  const [recordingChoices, setRecordingChoices] = useState<
    Record<string, string | null>
  >(() =>
    Object.fromEntries(
      setlist?.songs.flatMap((s) =>
        s.songId ? [[s.songId, s.recordingId ?? null]] : [],
      ) ?? [],
    ),
  );

  const selectedSongIds = useMemo(
    () => entries.flatMap((e) => ("songId" in e ? [e.songId] : [])),
    [entries],
  );

  const songsPayload = () =>
    entries.map((e) =>
      "songId" in e
        ? { songId: e.songId, recordingId: recordingChoices[e.songId] ?? null }
        : { title: e.title },
    );

  const createSetlistMutation = useMutation({
    mutationFn: async () => {
      const response = await api.setlists.$post({
        json: {
          name,
          notes: notes || undefined,
          unlisted,
          songs: songsPayload(),
        },
      });
      if (!response.ok) {
        throw new Error("Failed to create setlist");
      }
      return response.json();
    },
    onSuccess: (data) => {
      navigate({ to: "/setlists/$id", params: { id: data.setlistId } });
    },
    onError: (err) => {
      console.error("Create setlist error:", err);
      setError(err instanceof Error ? err.message : "Failed to create setlist");
    },
  });

  const updateSetlistMutation = useMutation({
    mutationFn: async () => {
      if (!setlist) throw new Error("No setlist to update");
      const response = await api.setlists[":id"].$patch({
        param: { id: setlist.id },
        json: {
          name,
          // Always send notes: the server preserves omitted notes for the
          // sake of clients that don't edit them (e.g. iOS).
          notes,
          unlisted,
          songs: songsPayload(),
        },
      });
      if (!response.ok) {
        throw new Error("Failed to update setlist");
      }
      return response.json();
    },
    onSuccess: () => {
      if (!isCreateMode) props.onSaved();
    },
    onError: (err) => {
      console.error("Update setlist error:", err);
      setError(err instanceof Error ? err.message : "Failed to update setlist");
    },
  });

  const deleteSetlistMutation = useMutation({
    mutationFn: async () => {
      if (!setlist) throw new Error("No setlist to delete");
      const response = await api.setlists[":id"].$delete({
        param: { id: setlist.id },
      });
      if (!response.ok) {
        throw new Error("Failed to delete setlist");
      }
      return response.json();
    },
    onSuccess: () => {
      navigate({ to: "/setlists" });
    },
    onError: (err) => {
      console.error("Delete setlist error:", err);
      throw err;
    },
  });

  const isPending =
    createSetlistMutation.isPending ||
    updateSetlistMutation.isPending ||
    deleteSetlistMutation.isPending;

  const handleCancel = () => {
    if (isCreateMode) {
      navigate({ to: "/setlists" });
    } else {
      props.onCancelled();
    }
  };

  const handleSubmit = () => {
    setError(null);
    if (isCreateMode) {
      createSetlistMutation.mutate();
    } else {
      updateSetlistMutation.mutate();
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit();
  };

  const handleAddSong = (songId: string) => {
    setEntries((prev) => [...prev, { key: songId, songId }]);
  };

  const trimmedTitle = newTitle.trim();
  const handleAddTitle = () => {
    if (!trimmedTitle) return;
    setEntries((prev) => [
      ...prev,
      { key: crypto.randomUUID(), title: trimmedTitle },
    ]);
    setNewTitle("");
  };

  const handleTitleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Enter adds the title rather than submitting the whole form
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTitle();
    }
  };

  const handleRemoveEntry = (key: string) => {
    setEntries((prev) => prev.filter((e) => e.key !== key));
  };

  const handleReorder = (keys: string[]) => {
    setEntries((prev) => {
      const byKey = new Map(prev.map((e) => [e.key, e]));
      return keys.flatMap((key) => byKey.get(key) ?? []);
    });
  };

  const handleDelete = async () => {
    await deleteSetlistMutation.mutateAsync();
  };

  const songMap = useMemo(
    () => new Map(allSongs.map((s) => [s.id, s])),
    [allSongs],
  );
  const setlistSongMap = useMemo(
    () =>
      new Map(
        setlist?.songs.flatMap((s) => (s.songId ? [[s.songId, s]] : [])) ?? [],
      ),
    [setlist?.songs],
  );

  // Rows, with each song enriched with its takes (only known for songs
  // already saved in the setlist — the all-songs list carries no takes)
  const selectedSongs = useMemo(
    () =>
      entries.flatMap((entry) => {
        if (!("songId" in entry)) {
          return [{ id: entry.key, songId: null, title: entry.title }];
        }
        const song = songMap.get(entry.songId);
        if (!song) return [];
        return [
          {
            ...song,
            id: entry.key,
            songId: entry.songId,
            recordings: setlistSongMap.get(entry.songId)?.song?.recordings,
            recordingId: recordingChoices[entry.songId] ?? null,
          },
        ];
      }),
    [entries, songMap, setlistSongMap, recordingChoices],
  );

  const handleRecordingChange = (songId: string, recordingId: string | null) =>
    setRecordingChoices((prev) => ({ ...prev, [songId]: recordingId }));

  return (
    <form onSubmit={handleFormSubmit}>
      <BackLink
        href="/setlists"
        actions={
          <FormActionButtons
            mode={isCreateMode ? "create" : "edit"}
            isPending={isPending}
            error={error}
            onCancel={handleCancel}
          />
        }
      >
        All Setlists
      </BackLink>

      <div className="mb-8">
        <label
          htmlFor="name"
          className="mb-2 block font-mono text-xs font-medium uppercase tracking-widest text-text-muted"
        >
          Name
        </label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isPending}
          placeholder="e.g., Sunday Rehearsal"
          required
          className="w-full rounded-lg border border-border-subtle bg-bg-secondary px-4 py-2 text-text-primary placeholder-text-muted transition-colors focus:border-amber focus:outline-none disabled:opacity-50"
        />
      </div>

      <div className="mb-8">
        <label
          htmlFor="notes"
          className="mb-2 block font-mono text-xs font-medium uppercase tracking-widest text-text-muted"
        >
          Notes
        </label>
        <textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onKeyDown={handleCmdEnterSubmit}
          disabled={isPending}
          placeholder="e.g., Focus on harmonies for Track 3"
          rows={3}
          className="w-full rounded-lg border border-border-subtle bg-bg-secondary px-4 py-2 text-text-primary placeholder-text-muted transition-colors focus:border-amber focus:outline-none disabled:opacity-50"
        />
      </div>

      <div className="mb-8">
        <CheckboxToggle
          checked={unlisted}
          onChange={setUnlisted}
          label="Unlisted"
        />
        <p className="mt-1 text-xs text-text-muted">
          Hidden from the Setlists page. Anyone with the link can still open it.
        </p>
      </div>

      {selectedSongs.length > 0 && (
        <div className="mb-8">
          <h3 className="mb-2 font-mono text-xs font-medium uppercase tracking-widest text-text-muted">
            {isCreateMode ? "Selected Songs" : "Songs"} ({selectedSongs.length})
          </h3>
          <SetlistSongsList
            songs={selectedSongs}
            isEditing={true}
            disabled={isPending}
            onReorder={handleReorder}
            onRemove={handleRemoveEntry}
            onRecordingChange={handleRecordingChange}
          />
        </div>
      )}

      <div className="mb-8">
        <h3 className="mb-2 font-mono text-xs font-medium uppercase tracking-widest text-text-muted">
          Add Songs
        </h3>
        <SongPicker
          songs={allSongs}
          selectedSongIds={selectedSongIds}
          onAddSong={handleAddSong}
          disabled={isPending}
        />
      </div>

      <div className="mb-8">
        <label
          htmlFor="new-title"
          className="mb-2 block font-mono text-xs font-medium uppercase tracking-widest text-text-muted"
        >
          Add a Song Not in Bandwith
        </label>
        <div className="flex gap-2">
          <input
            id="new-title"
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={handleTitleKeyDown}
            disabled={isPending}
            placeholder="e.g., Wonderwall (Oasis cover)"
            className="min-w-0 flex-1 rounded-lg border border-border-subtle bg-bg-secondary px-4 py-2 text-text-primary placeholder-text-muted transition-colors focus:border-amber focus:outline-none disabled:opacity-50"
          />
          <Button
            type="button"
            variant="secondary"
            onClick={handleAddTitle}
            disabled={isPending || !trimmedTitle}
            leftIcon={Plus}
          >
            Add
          </Button>
        </div>
        <p className="mt-1 text-xs text-text-muted">
          For covers and other songs you don't keep here. It shows by title,
          with no page or recording.
        </p>
      </div>

      {!isCreateMode && (
        <section className="border-t border-border-subtle pt-8">
          <h2 className="mb-4 font-mono text-xs font-medium uppercase tracking-widest text-red-muted">
            Danger Zone
          </h2>
          <DeleteConfirmation
            onDelete={handleDelete}
            itemName="Setlist"
            disabled={isPending}
          />
        </section>
      )}
    </form>
  );
}
